// ====================================================
// src/components/block/liveStreamManagement/videoEffects/DecorationCalibrationModal.tsx
//
// Panneau de calibration d'UNE décoration (Couronne, Perruque, Lunettes, Moustache,
// Bague...) — au lieu de deviner position/rotation/taille à l'aveugle (constaté
// hors-cible à plusieurs reprises en régie réelle), affiche le modèle ACTUELLEMENT
// SÉLECTIONNÉ pour "Visage"/"Mains" (cf. AvatarConfig.faceModelId/handModelId,
// AvatarRenderer.loadReferenceModel) vu de face, de dessus et de profil, avec la
// décoration posée dessus — les axes affichés (AxesHelper) sont ceux du point
// d'ancrage lui-même (tête/main), au centre des trois vues. Position/rotation/échelle
// s'ajustent soit par curseur, soit directement à la souris sur n'importe laquelle des
// 3 vues (cf. handlePointerMove) — chaque vue ne bouge que les DEUX axes qu'elle montre
// (face → x/y, dessus → x/z, profil → z/y), donc les trois ensemble couvrent x/y/z.
//
// Le résultat (cf. DecorationTransform) est ensuite RÉELLEMENT utilisé en régie par
// AvatarRenderer (updateFaceSlotTransform/updateHandSlotTransform) : la référence
// affichée ici (générique OU le vrai modèle choisi) est construite/instanciée avec
// EXACTEMENT le même pivot qu'en régie (cf. instantiate()) et la tête/main générique
// a sa distance inter-yeux / largeur de paume qui vaut EXACTEMENT 1 unité — la même
// unité que DecorationTransform.position — donc ce qui se voit ici se transpose
// directement, pas une approximation.
// ====================================================

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as THREE from 'three';
import { X, RotateCcw } from 'lucide-react';
import { AvatarModelDef, DecorationTransform, DEFAULT_DECORATION_TRANSFORM, GENERIC_HEAD_HEIGHT, GENERIC_HAND_HEIGHT } from './AvatarModels';
import { buildReferenceHead, buildReferenceHand } from './AvatarProceduralModels';
import { loadReferenceModel, buildDecorationReferenceMesh } from './AvatarRenderer';
import { LevelSlider } from '../LevelSlider';

const VIEW_SIZE = 240; // px — taille INTERNE de CHAQUE vignette (face/dessus/profil)
const ORTHO_HALF_EXTENT = 1.7; // couvre la tête/main de référence (rayon ~1) avec un peu de marge

interface Props {
  decoration: AvatarModelDef;
  /** Modèle actuellement choisi pour la famille de CETTE décoration (cf.
   * AvatarConfig.faceModelId pour une décoration ancrée face/eyes/aboveHead,
   * handModelId pour 'hand') — null si aucun, auquel cas on retombe sur une tête/
   * main générique (cf. buildReferenceHead/Hand). */
  referenceModel: AvatarModelDef | null;
  initialTransform: DecorationTransform;
  onSave: (transform: DecorationTransform) => void;
  onClose: () => void;
}

// Le repère (AxesHelper, à l'origine) doit être centré sur le VISAGE (ou la main),
// TOUJOURS — jamais au-dessus, jamais à côté, jamais devant : le centre géométrique
// pur de la boîte englobante (X/Y/Z), dans les 3 vues, y compris en profondeur. Un
// essai précédent avait déplacé le Z sur l'avant du visage (racine du nez) pour
// coller à ce que calculait alors la régie — choix explicitement écarté : demandé de
// garder le centre ICI et de faire correspondre la RÉGIE à ce même repère plutôt que
// l'inverse (cf. AvatarRenderer.updateFaceSlotTransform, CENTER_OF_HEAD_DEPTH_OFFSET).
function computeReferenceAnchorPoint(box: THREE.Box3): THREE.Vector3 {
  return box.getCenter(new THREE.Vector3());
}

function makeOrthoCamera(): THREE.OrthographicCamera {
  // THREE.OrthographicCamera(left, right, top, bottom, near, far) — `bottom` DOIT être
  // négatif : avec +ORTHO_HALF_EXTENT ici (erreur précédente), top === bottom donnait un
  // frustum de hauteur NULLE, la matrice de projection divisant par (top-bottom)=0 —
  // tout se projetait à des coordonnées dégénérées, donc AUCUNE géométrie visible, sans
  // la moindre erreur JS (WebGL rejette silencieusement les primitives invalides).
  return new THREE.OrthographicCamera(-ORTHO_HALF_EXTENT, ORTHO_HALF_EXTENT, ORTHO_HALF_EXTENT, -ORTHO_HALF_EXTENT, 0.01, 20);
}

export const DecorationCalibrationModal: React.FC<Props> = ({ decoration, referenceModel, initialTransform, onSave, onClose }) => {
  const [transform, setTransform] = useState<DecorationTransform>(initialTransform);
  const [referenceLoading, setReferenceLoading] = useState(!!referenceModel);
  // Distinct de `!referenceModel` : un modèle EST sélectionné mais s'avère sans pose
  // statique exploitable (rig de main, cf. loadReferenceModel qui renvoie null) ou en
  // échec de chargement — dans les deux cas on affiche quand même la générique, mais
  // le texte sous les vues doit le dire plutôt que prétendre montrer le vrai modèle.
  const [usingGenericFallback, setUsingGenericFallback] = useState(false);
  // cf. AvatarModels.DecorationTransform.centerDepthOffset — une mesure automatique sur
  // le modèle affiché (cf. attachReference) s'est révélée retomber sur ma propre
  // approximation générique (sphère bien moins profonde qu'un vrai crâne humain,
  // rapport réel tête/écart inter-yeux ≈ 19-20cm/6,3cm ≈ 3, donc demi-profondeur ≈ 1,5
  // pas 0,95) plutôt qu'une vraie mesure fiable — exposé en curseur DIRECT ici : plus
  // fiable de laisser l'utilisateur l'ajuster en se voyant en régie réelle (qu'il peut
  // voir, contrairement à moi) que de continuer à deviner une constante à l'aveugle.
  const [centerDepthOffset, setCenterDepthOffset] = useState(initialTransform.centerDepthOffset);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const decorationMeshRef = useRef<THREE.Object3D | null>(null);
  // targetRatio/tailleNative — cf. AvatarRenderer.updateFaceSlotTransform/
  // updateHandSlotTransform : `slot.group.scale.setScalar(worldSize/loaded.size)`
  // APPLIQUÉ AVANT `decorationTransform.scale` (cf. `.multiply()` juste après en régie).
  // Sans ce même facteur de base ICI, "Échelle = 1×" dans ce panneau montrait la
  // décoration à sa taille NATIVE brute — une toute autre taille que celle réellement
  // rendue en régie (qui, elle, part de targetRatio × distance inter-yeux) : ce que
  // l'utilisateur calibrait visuellement ne correspondait donc à RIEN de réel une fois
  // appliqué (constaté : "la taille n'est pas celle qu'on a choisie, la position
  // n'importe quoi" — cette dernière n'était qu'un symptôme du décalage d'échelle,
  // la même valeur de position ne "tombant" plus au même endroit visuellement une fois
  // la taille réelle très différente de celle affichée ici). 1 par défaut tant que la
  // décoration n'est pas encore construite (cf. mount effect ci-dessous).
  const baselineScaleRef = useRef(1);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const scenesRef = useRef<{ scene: THREE.Scene; front: THREE.OrthographicCamera; top: THREE.OrthographicCamera; side: THREE.OrthographicCamera } | null>(null);

  // Redessine les 3 vues avec l'état ACTUEL (transform appliqué à la décoration) —
  // extrait en fonction séparée : appelé aussi bien à chaque changement de curseur/
  // glisser-déposer qu'une fois le vrai modèle de référence chargé (asynchrone).
  const redraw = () => {
    const renderer = rendererRef.current;
    const scenes = scenesRef.current;
    const mesh = decorationMeshRef.current;
    if (!renderer || !scenes || !mesh) return;
    mesh.position.set(...transform.position);
    mesh.rotation.set(...transform.rotation);
    const b = baselineScaleRef.current;
    mesh.scale.set(transform.scale[0] * b, transform.scale[1] * b, transform.scale[2] * b);
    const draw = (camera: THREE.OrthographicCamera, x: number) => {
      renderer.setViewport(x, 0, VIEW_SIZE, VIEW_SIZE);
      renderer.setScissor(x, 0, VIEW_SIZE, VIEW_SIZE);
      renderer.render(scenes.scene, camera);
    };
    draw(scenes.front, 0);
    draw(scenes.top, VIEW_SIZE);
    draw(scenes.side, VIEW_SIZE * 2);
  };

  // Toujours la dernière closure de `redraw` (donc le dernier `transform`) — le
  // callback de chargement asynchrone ci-dessous (mount effect, deps figées à [])
  // ne doit PAS redessiner avec le transform de la toute première frame si
  // l'utilisateur a déjà bougé un curseur pendant que le modèle glTF téléchargeait.
  const redrawRef = useRef(redraw);
  redrawRef.current = redraw;

  // Montage — une seule fois : renderer + scène + décoration + 3 caméras
  // orthographiques partageant la MÊME scène (rendues chacune dans une portion du
  // canvas via setViewport/setScissor). La référence (tête/main, générique ou vrai
  // modèle choisi) est ajoutée séparément ci-dessous — synchrone pour la générique,
  // asynchrone (chargement glTF) pour un vrai modèle.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(0x0f172a, 1);
    renderer.setSize(VIEW_SIZE * 3, VIEW_SIZE, false);
    renderer.setScissorTest(true);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 1.1));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(1, 1.5, 2);
    scene.add(dirLight);

    // Même pivot que la régie réelle (cf. buildDecorationReferenceMesh) — sinon
    // l'origine locale de la décoration (donc sa position par rapport au repère
    // affiché, centré sur le point d'ancrage) ne correspond pas à ce qui se passe
    // réellement en régie.
    const decorationMesh = decoration.build ? buildDecorationReferenceMesh(decoration) : new THREE.Group();
    scene.add(decorationMesh);
    decorationMeshRef.current = decorationMesh;
    // cf. baselineScaleRef — même formule que le `scale` de base calculé en régie
    // (targetRatio / plus grande dimension native), mesurée sur CE maillage avant tout
    // redraw (le pivot déjà appliqué par buildDecorationReferenceMesh ne change pas sa
    // taille, seulement sa position).
    const nativeSize = new THREE.Box3().setFromObject(decorationMesh).getSize(new THREE.Vector3());
    const nativeMaxDim = Math.max(nativeSize.x, nativeSize.y, nativeSize.z) || 1;
    baselineScaleRef.current = decoration.targetRatio / nativeMaxDim;

    scene.add(new THREE.AxesHelper(1.9));

    const front = makeOrthoCamera(); front.position.set(0, 0, 5); front.lookAt(0, 0, 0);
    const top   = makeOrthoCamera(); top.position.set(0, 5, 0); top.up.set(0, 0, -1); top.lookAt(0, 0, 0);
    const side  = makeOrthoCamera(); side.position.set(5, 0, 0); side.lookAt(0, 0, 0);
    scenesRef.current = { scene, front, top, side };

    let cancelled = false;
    let referenceObject: THREE.Object3D | null = null;

    const attachReference = (obj: THREE.Object3D) => {
      if (cancelled) return; // panneau refermé (ou modèle changé) avant la fin du chargement
      referenceObject = obj;
      scene.add(obj);
      obj.updateMatrixWorld(true);

      // Recale sur la hauteur EXACTE de la tête/main générique (cf. GENERIC_HEAD_HEIGHT/
      // GENERIC_HAND_HEIGHT) — un vrai modèle chargé (facecap, renard...) sort de
      // loadReferenceModel() mis à l'échelle via SON PROPRE targetRatio (cf. AvatarModels.
      // ts : "pas calibré sur un vrai visage", une simple estimation de sa hauteur globale
      // en multiples de distance inter-yeux) — RIEN ne garantit que SA distance inter-yeux
      // à lui tombe exactement sur 1 unité une fois mis à l'échelle. Résultat constaté :
      // une position calibrée qui semble juste dans CE panneau (contre CE modèle-là) ne
      // l'est plus une fois appliquée au VRAI visage suivi en régie (dont l'unité, elle,
      // EST la vraie distance inter-yeux mesurée). Recaler sur la même hauteur globale que
      // la tête/main générique (dont la distance inter-yeux VAUT exactement 1 par
      // construction, cf. buildReferenceHead/Hand) est un correctif approché — pas une
      // mesure réelle des yeux de CE modèle précis, dont on n'a pas les repères — mais
      // bien plus proche de la vérité qu'un targetRatio jamais vérifié. No-op sur la tête/
      // main générique elle-même (déjà à cette hauteur exacte, facteur ≈ 1).
      const preBox = new THREE.Box3().setFromObject(obj);
      const preHeight = preBox.getSize(new THREE.Vector3()).y || 1;
      const targetHeight = decoration.anchor === 'hand' ? GENERIC_HAND_HEIGHT : GENERIC_HEAD_HEIGHT;
      obj.scale.multiplyScalar(targetHeight / preHeight);
      obj.updateMatrixWorld(true);

      // Recentre pour que le point d'ancrage de CETTE décoration (aboveHead pour une
      // couronne, eyes pour des lunettes...) — PAS le pivot propre au modèle de
      // remplacement chargé (visage/renard/poisson) — tombe exactement à l'origine,
      // là où le repère (AxesHelper) et la décoration sont déjà posés.
      const box = new THREE.Box3().setFromObject(obj);
      const anchor = computeReferenceAnchorPoint(box);
      obj.position.sub(anchor);
      // Pas de mesure automatique de la profondeur ici — essayé, mais retombe sur une
      // approximation trop plate (essentiellement la même sous-estimation qu'une
      // constante devinée, cf. centerDepthOffset ci-dessus/curseur "Profondeur"). Le
      // curseur reste la source de vérité, réglée par l'utilisateur en régie réelle.
      setReferenceLoading(false);
      redrawRef.current();
    };
    const genericReference = () => { if (!cancelled) setUsingGenericFallback(true); return decoration.anchor === 'hand' ? buildReferenceHand() : buildReferenceHead(); };

    if (referenceModel) {
      loadReferenceModel(referenceModel)
        .then(obj => attachReference(obj ?? genericReference())) // null = rig de main (cf. loadReferenceModel), pas de pose statique exploitable
        .catch(err => {
          console.warn(`[DecorationCalibrationModal] échec de chargement du modèle de référence "${referenceModel.id}" :`, err);
          attachReference(genericReference());
        });
    } else {
      attachReference(genericReference());
    }

    return () => {
      cancelled = true;
      if (referenceObject) scene.remove(referenceObject);
      renderer.dispose();
      rendererRef.current = null;
      scenesRef.current = null;
      decorationMeshRef.current = null;
    };
    // decoration/referenceModel ne changent jamais pendant la durée de vie de CE
    // panneau (une instance par ouverture du ⚙, refermée avant d'en rouvrir un autre).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Applique `transform` au maillage de la décoration puis redessine les 3 vues —
  // à chaque changement de curseur/glisser-déposer, PAS de nouvelle géométrie.
  useEffect(() => { redraw(); }, [transform]);

  const setAxis = (key: 'position' | 'rotation' | 'scale', axis: 0 | 1 | 2, value: number) => {
    setTransform(t => {
      const next: [number, number, number] = [...t[key]];
      next[axis] = value;
      return { ...t, [key]: next };
    });
  };

  // ── Glisser-déposer directement sur une vue ────────────────────────────────────
  // Chaque vue ne montre que 2 des 3 axes (face → x/y, dessus → x/z, profil → z/y,
  // cf. l'orientation de chaque caméra ci-dessus) — on déplace donc SEULEMENT ce que
  // CETTE vue montre, jamais le 3e axe (qui, lui, se règle en glissant sur une des deux
  // AUTRES vues, ou au curseur). Le delta souris (en pixels CSS) est converti en unités
  // MONDE via l'étendue orthographique connue (ORTHO_HALF_EXTENT), puis projeté sur les
  // axes RÉELS "droite"/"haut" de la caméra concernée (quaternion) — générique, marche
  // pour les 3 caméras sans cas particulier par vue.
  const draggingRef = useRef<{ camera: THREE.OrthographicCamera; lastClientX: number; lastClientY: number } | null>(null);

  const cameraAtClientX = (clientX: number): THREE.OrthographicCamera | null => {
    const canvas = canvasRef.current;
    const scenes = scenesRef.current;
    if (!canvas || !scenes) return null;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return null;
    const internalX = ((clientX - rect.left) / rect.width) * (VIEW_SIZE * 3);
    const viewportIndex = Math.max(0, Math.min(2, Math.floor(internalX / VIEW_SIZE)));
    return [scenes.front, scenes.top, scenes.side][viewportIndex];
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const camera = cameraAtClientX(e.clientX);
    if (!camera) return;
    draggingRef.current = { camera, lastClientX: e.clientX, lastClientY: e.clientY };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const drag = draggingRef.current;
    const canvas = canvasRef.current;
    if (!drag || !canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    let deltaCssX = e.clientX - drag.lastClientX;
    let deltaCssY = e.clientY - drag.lastClientY;
    drag.lastClientX = e.clientX;
    drag.lastClientY = e.clientY;
    if (deltaCssX === 0 && deltaCssY === 0) return;

    // CTRL enfoncé : verrouille le glissement sur UN SEUL des deux axes que cette vue
    // montre — celui qui domine le mouvement de CE pas (annule l'autre composante).
    // Ex. en vue de face (x/y) : si le geste est surtout horizontal, y reste figé tant
    // que CTRL est tenu, même si la souris dérive un peu verticalement entre-temps.
    if (e.ctrlKey) {
      if (Math.abs(deltaCssX) >= Math.abs(deltaCssY)) deltaCssY = 0;
      else deltaCssX = 0;
    }

    // Unités monde par pixel CSS — l'étendue orthographique (2×ORTHO_HALF_EXTENT)
    // couvre VIEW_SIZE px INTERNES par vue, eux-mêmes affichés sur (largeur totale du
    // canvas à l'écran)/3 px CSS en largeur, ou la hauteur totale en hauteur.
    const worldPerCssX = (2 * ORTHO_HALF_EXTENT) / (rect.width / 3);
    const worldPerCssY = (2 * ORTHO_HALF_EXTENT) / rect.height;
    const deltaCamRight = deltaCssX * worldPerCssX;
    const deltaCamUp = -deltaCssY * worldPerCssY; // écran Y vers le bas ↔ monde "haut" inversé

    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(drag.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(drag.camera.quaternion);
    const worldDelta = right.multiplyScalar(deltaCamRight).add(up.multiplyScalar(deltaCamUp));

    setTransform(t => ({
      ...t,
      position: [t.position[0] + worldDelta.x, t.position[1] + worldDelta.y, t.position[2] + worldDelta.z],
    }));
  };

  const endDrag = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!draggingRef.current) return;
    draggingRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // Portail vers document.body — comme TOUS les autres modaux "position: fixed" du
  // projet (SourceConfigModal, RegieConfigModal...). Sans ça, ce panneau reste
  // DESCENDANT DOM de l'overlay de SourceConfigModal, qui a un backdropFilter — un
  // ancêtre filtré redevient le "containing block" d'un position:fixed (spec CSS),
  // donc `inset: 0` ne couvre alors plus le vrai viewport mais seulement la boîte de
  // cet ancêtre.
  return ReactDOM.createPortal(
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#0f172a', border: '1px solid #334155', borderRadius: 12,
        padding: 24, width: 900, maxWidth: '96vw', maxHeight: '94vh', overflowY: 'auto',
        display: 'flex', flexDirection: 'column', gap: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#e2e8f0' }}>
            Calibrer — {decoration.label}
          </span>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
            <X style={{ width: 18, height: 18 }} />
          </button>
        </div>

        <div style={{ fontSize: 11, color: 'rgba(148,163,184,0.7)', lineHeight: 1.4 }}>
          {referenceModel && !usingGenericFallback
            ? `Modèle "${referenceModel.label}" actuellement sélectionné — ${decoration.anchor === 'hand' ? 'largeur de paume' : 'distance entre les yeux'} = 1 unité, même échelle que le réglage.`
            : (decoration.anchor === 'hand'
              ? `Main de référence générique (largeur de paume = 1 unité)${referenceModel ? ' — pas de pose statique pour un rig de main hors suivi réel' : ' — aucun modèle "Mains" sélectionné'}.`
              : `Tête de référence générique (distance entre les yeux = 1 unité)${referenceModel ? ' — échec de chargement du modèle sélectionné' : ' — aucun modèle "Visage" sélectionné'}.`)}
          {' '}Glisse directement sur une vue pour déplacer — maintiens Ctrl pour verrouiller sur un seul axe.
        </div>

        <div style={{ position: 'relative', width: '100%', aspectRatio: '3 / 1' }}>
          <canvas
            ref={canvasRef} width={VIEW_SIZE * 3} height={VIEW_SIZE}
            onPointerDown={handlePointerDown} onPointerMove={handlePointerMove}
            onPointerUp={endDrag} onPointerLeave={endDrag} onPointerCancel={endDrag}
            style={{ display: 'block', borderRadius: 8, width: '100%', height: '100%', cursor: 'grab', touchAction: 'none' }}
          />
          {referenceLoading && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, color: '#94a3b8', background: 'rgba(15,23,42,0.6)', borderRadius: 8, pointerEvents: 'none',
            }}>
              Chargement du modèle…
            </div>
          )}
          <div style={{ display: 'flex', fontSize: 10, color: 'rgba(148,163,184,0.6)', marginTop: 4 }}>
            <span style={{ flex: 1, textAlign: 'center' }}>Face</span>
            <span style={{ flex: 1, textAlign: 'center' }}>Dessus</span>
            <span style={{ flex: 1, textAlign: 'center' }}>Profil</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <LevelSlider label="Position X" value={transform.position[0]} min={-2} max={2} defaultValue={0} roundTo={0.05}
            format={v => v.toFixed(2)} onChange={v => setAxis('position', 0, v)} />
          <LevelSlider label="Position Y" value={transform.position[1]} min={-2} max={2} defaultValue={0} roundTo={0.05}
            format={v => v.toFixed(2)} onChange={v => setAxis('position', 1, v)} />
          <LevelSlider label="Position Z" value={transform.position[2]} min={-2} max={2} defaultValue={0} roundTo={0.05}
            format={v => v.toFixed(2)} onChange={v => setAxis('position', 2, v)} />

          <LevelSlider label="Rotation X" value={transform.rotation[0]} min={-Math.PI} max={Math.PI} defaultValue={0} roundTo={0.02}
            format={v => `${Math.round(v * 180 / Math.PI)}°`} onChange={v => setAxis('rotation', 0, v)} />
          <LevelSlider label="Rotation Y" value={transform.rotation[1]} min={-Math.PI} max={Math.PI} defaultValue={0} roundTo={0.02}
            format={v => `${Math.round(v * 180 / Math.PI)}°`} onChange={v => setAxis('rotation', 1, v)} />
          <LevelSlider label="Rotation Z" value={transform.rotation[2]} min={-Math.PI} max={Math.PI} defaultValue={0} roundTo={0.02}
            format={v => `${Math.round(v * 180 / Math.PI)}°`} onChange={v => setAxis('rotation', 2, v)} />

          <LevelSlider label="Échelle X" value={transform.scale[0]} min={0.2} max={3} defaultValue={1} roundTo={0.05}
            format={v => `${v.toFixed(2)}×`} onChange={v => setAxis('scale', 0, v)} />
          <LevelSlider label="Échelle Y" value={transform.scale[1]} min={0.2} max={3} defaultValue={1} roundTo={0.05}
            format={v => `${v.toFixed(2)}×`} onChange={v => setAxis('scale', 1, v)} />
          <LevelSlider label="Échelle Z" value={transform.scale[2]} min={0.2} max={3} defaultValue={1} roundTo={0.05}
            format={v => `${v.toFixed(2)}×`} onChange={v => setAxis('scale', 2, v)} />
        </div>

        <div>
          <LevelSlider label="Profondeur (centre de la tête/main)" value={centerDepthOffset} min={0} max={3} defaultValue={1.5} roundTo={0.05}
            format={v => v.toFixed(2)} onChange={setCenterDepthOffset} />
          <div style={{ fontSize: 10, color: 'rgba(148,163,184,0.6)', marginTop: 2 }}>
            Sans effet visible ICI (les 3 vues sont déjà centrées) — seulement en régie
            réelle, où ce point ne peut pas être mesuré sur un modèle. Augmente si la
            décoration reste trop en avant en régie, même après avoir réglé Position Z.
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 4 }}>
          <button type="button" onClick={() => {
            const def = decoration.defaultTransform ?? DEFAULT_DECORATION_TRANSFORM;
            setTransform(def);
            setCenterDepthOffset(def.centerDepthOffset);
          }}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
              border: '1px solid #475569', background: 'rgba(255,255,255,0.03)', color: '#94a3b8', cursor: 'pointer', fontSize: 12,
            }}>
            <RotateCcw style={{ width: 14, height: 14 }} /> Réinitialiser
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={onClose}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #475569', background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>
              Annuler
            </button>
            <button type="button" onClick={() => onSave({ ...transform, centerDepthOffset })}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#38bdf8', color: '#0f172a', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
